// RigLogic joint deltas onto a glTF face skeleton — the pure half.
//
// A MetaHuman published as a GLB carries its face as 875 skinned joints and no
// ARKit blendshapes (measured on Aoi: 0 morph targets on any mesh), so the
// morph-driven face path has nothing to move. But the DNA that drives those
// joints in Unreal drives them here too: OpenRigLogic (src/lib/orl) evaluates
// the same rig and hands back one local delta per joint. This module turns those
// deltas into bone poses. No three.js import, so node:test can pin the maths to
// the deformer's own composition (deform.js) — the two must agree exactly, since
// a splat head skinned by that deformer and a GLB head posed by this file are
// the same rig seen twice.
//
// COORDINATES. The DNA is Maya space (cm, Y-up); the glTF exporter re-expressed
// every node's LOCAL transform in glTF space. Measured on Aoi: all 869 non-root
// joints' rest transforms equal the DNA neutral after ONE fixed change of basis,
//   C : (x, y, z) -> (x, z, -y)   (a rotation of -90 deg about x),
// to 0.28 um / 0.03 deg. A change of basis conjugates every local matrix,
// L_glb = C L_dna C^-1, and since the posed local is L_neutral * D, the GLB pose
// is rest * (C D C^-1): rotate the translation delta, permute the quaternion's
// vector part the same way, permute the scale axes. The root joint (spine_04,
// whose DNA transform carries the world placement) and the four body joints the
// mixer animates are deliberately not bound — see bindFaceJoints.
//
// RigLogic's per-joint output is 9 floats — tx ty tz (cm) rx ry rz (degrees,
// Maya extrinsic xyz, i.e. R = Rz*Ry*Rx) sx sy sz (scale DELTA, scale = 1 + s) —
// and the posed local is T(t_n + t_d) * R(r_n) * R(r_d) * S(1 + s_d) (deform.js).

export const JOINT_STRIDE = 9
export const CM_TO_M = 0.01
/** Joints the DNA drives that the BODY animation owns on a GLB rig are the ones WITHOUT this prefix. */
export const FACE_JOINT_PREFIX = 'FACIAL_'

const DEG = Math.PI / 180

/** Maya extrinsic xyz (degrees) -> quaternion [x,y,z,w] at out[o..o+3]: q = qz * qy * qx. */
export function eulerXyzDegToQuat(rx, ry, rz, out, o = 0) {
  const hx = rx * DEG * 0.5, hy = ry * DEG * 0.5, hz = rz * DEG * 0.5
  const sx = Math.sin(hx), cx = Math.cos(hx)
  const sy = Math.sin(hy), cy = Math.cos(hy)
  const sz = Math.sin(hz), cz = Math.cos(hz)
  // qz * qy * qx, expanded
  out[o]     = sx * cy * cz - cx * sy * sz
  out[o + 1] = cx * sy * cz + sx * cy * sz
  out[o + 2] = cx * cy * sz - sx * sy * cz
  out[o + 3] = cx * cy * cz + sx * sy * sz
  return out
}

/** out = a * b (Hamilton product), all as [x,y,z,w] at the given offsets. Safe when out aliases a or b. */
export function quatMul(a, ao, b, bo, out, oo) {
  const ax = a[ao], ay = a[ao + 1], az = a[ao + 2], aw = a[ao + 3]
  const bx = b[bo], by = b[bo + 1], bz = b[bo + 2], bw = b[bo + 3]
  out[oo]     = aw * bx + ax * bw + ay * bz - az * by
  out[oo + 1] = aw * by - ax * bz + ay * bw + az * bx
  out[oo + 2] = aw * bz + ax * by - ay * bx + az * bw
  out[oo + 3] = aw * bw - ax * bx - ay * by - az * bz
  return out
}

/**
 * Binds the DNA's joints to a skeleton by name.
 *
 * Only joints whose name starts with `prefix` are bound: the DNA also carries
 * spine_04..head, and on a GLB those belong to the body clips the mixer plays
 * (the face rig's neutral for them is the DNA's world placement, not this rig's).
 * Missing names are REPORTED, never skipped silently — a bone the DNA drives
 * and the GLB lacks means the rig was exported from a different asset.
 *
 * @param {string[]} jointNames DNA joint order (the pack manifest's jointNames)
 * @param {Map<string, object>|((name:string)=>object|undefined)} boneByName
 * @returns {{ count:number, dnaIndex:Int32Array, bones:object[], rest:{pos:Float32Array,quat:Float32Array,scale:Float32Array}, missing:string[], bodyJoints:string[] }}
 */
export function bindFaceJoints(jointNames, boneByName, { prefix = FACE_JOINT_PREFIX } = {}) {
  const lookup = typeof boneByName === 'function' ? boneByName : (n) => boneByName.get(n)
  const dnaIndex = [], bones = [], missing = [], bodyJoints = []
  for (let j = 0; j < jointNames.length; j++) {
    const name = jointNames[j]
    if (!name.startsWith(prefix)) { bodyJoints.push(name); continue }
    const bone = lookup(name)
    if (!bone) { missing.push(name); continue }
    dnaIndex.push(j); bones.push(bone)
  }
  const n = bones.length
  const rest = { pos: new Float32Array(n * 3), quat: new Float32Array(n * 4), scale: new Float32Array(n * 3) }
  for (let i = 0; i < n; i++) {
    const b = bones[i]
    rest.pos[i * 3] = b.position.x; rest.pos[i * 3 + 1] = b.position.y; rest.pos[i * 3 + 2] = b.position.z
    rest.quat[i * 4] = b.quaternion.x; rest.quat[i * 4 + 1] = b.quaternion.y; rest.quat[i * 4 + 2] = b.quaternion.z; rest.quat[i * 4 + 3] = b.quaternion.w
    rest.scale[i * 3] = b.scale.x; rest.scale[i * 3 + 1] = b.scale.y; rest.scale[i * 3 + 2] = b.scale.z
  }
  return { count: n, dnaIndex: Int32Array.from(dnaIndex), bones, rest, missing, bodyJoints }
}

const _qd = new Float32Array(4)
const _q = new Float32Array(4)

/**
 * Posed local TRS of bound joint `i` from RigLogic's joint output, in glTF space.
 * Writes pos[3] (m), quat[4], scale[3] into `out` at `o`; allocation-free.
 *
 * pos   = rest.pos + C(t_d) * 0.01
 * quat  = rest.quat * C(q(r_d))
 * scale = rest.scale * (1 + C(s_d))
 */
export function poseJoint(binding, i, jointOut, out, o = 0) {
  const j = binding.dnaIndex[i] * JOINT_STRIDE
  const tdx = jointOut[j], tdy = jointOut[j + 1], tdz = jointOut[j + 2]
  const { pos, quat, scale } = binding.rest
  // translation: C(x,y,z) = (x, z, -y), centimetres -> metres
  out[o]     = pos[i * 3]     + tdx * CM_TO_M
  out[o + 1] = pos[i * 3 + 1] + tdz * CM_TO_M
  out[o + 2] = pos[i * 3 + 2] - tdy * CM_TO_M
  // rotation: conjugate the delta quaternion by C (vector part permutes like a vector)
  eulerXyzDegToQuat(jointOut[j + 3], jointOut[j + 4], jointOut[j + 5], _qd, 0)
  _q[0] = _qd[0]; _q[1] = _qd[2]; _q[2] = -_qd[1]; _q[3] = _qd[3]
  quatMul(quat, i * 4, _q, 0, out, o + 3)
  // scale: axes permute, signs irrelevant
  out[o + 7] = scale[i * 3]     * (1 + jointOut[j + 6])
  out[o + 8] = scale[i * 3 + 1] * (1 + jointOut[j + 8])
  out[o + 9] = scale[i * 3 + 2] * (1 + jointOut[j + 7])
  return out
}

const _trs = new Float32Array(10)

function writeTRS(b, t) {
  const p = b.position, q = b.quaternion, s = b.scale
  if (typeof p.set === 'function') p.set(t[0], t[1], t[2]); else { p.x = t[0]; p.y = t[1]; p.z = t[2] }
  if (typeof q.set === 'function') q.set(t[3], t[4], t[5], t[6]); else { q.x = t[3]; q.y = t[4]; q.z = t[5]; q.w = t[6] }
  if (typeof s.set === 'function') s.set(t[7], t[8], t[9]); else { s.x = t[7]; s.y = t[8]; s.z = t[9] }
}

/**
 * Writes every bound joint's pose into its bone. `jointOut` is RigLogic's
 * getJointOutputs() view (valid for this call only). Allocation-free.
 * @returns {number} joints written
 */
export function applyJointPoses(binding, jointOut) {
  const n = binding.count
  for (let i = 0; i < n; i++) {
    poseJoint(binding, i, jointOut, _trs, 0)
    writeTRS(binding.bones[i], _trs)
  }
  return n
}

/** Puts every bound bone back at its rest pose (character switch, driver teardown). */
export function restoreRest(binding) {
  const { pos, quat, scale } = binding.rest
  for (let i = 0; i < binding.count; i++) {
    _trs[0] = pos[i * 3]; _trs[1] = pos[i * 3 + 1]; _trs[2] = pos[i * 3 + 2]
    _trs[3] = quat[i * 4]; _trs[4] = quat[i * 4 + 1]; _trs[5] = quat[i * 4 + 2]; _trs[6] = quat[i * 4 + 3]
    _trs[7] = scale[i * 3]; _trs[8] = scale[i * 3 + 1]; _trs[9] = scale[i * 3 + 2]
    writeTRS(binding.bones[i], _trs)
  }
}
